import type { ComparePageCopy, ComparePageCopyByLang } from "./compare";

const VENDOR = { name: "Otter.ai", url: "https://otter.ai" };

const en: ComparePageCopy = {
  seo: {
    title: "Otter.ai vs. Minuta — notes after or answers live",
    description:
      "Honest comparison between Otter.ai and Minuta. Choose Otter for meeting notes, transcripts, search, and team workflows. Choose Minuta when you need help answering live in English, Portuguese, or Spanish.",
  },
  vendor: VENDOR,
  eyebrow: "Comparison",
  h1: "Otter.ai vs. Minuta: notes after, or answers live?",
  intro:
    "Otter helps teams capture, summarize, search, and share what happened after a meeting. Minuta helps professionals understand the room and draft a response while the customer is still waiting.",
  honesty: {
    heading: "Pick the right job",
    pickThem: {
      name: "Otter.ai",
      bullets: [
        "You want meeting notes, transcripts, summaries, and searchable history after the call.",
        "You're fine with a bot joining the call (Otter Assistant on Zoom/Meet/Teams).",
        "Your team already lives in Otter's web app for search, sharing, and follow-up workflows.",
        "You do not need help formulating an answer while the call is still happening.",
      ],
    },
    pickMinuta: [
      "You handle customer calls in English, Portuguese, or Spanish and need live help answering.",
      "You need to formulate a clear answer live, using the transcript and your own docs.",
      "You can't (or don't want to) have a bot show up in client calls.",
      "Your audio is sensitive — you want secure transient cloud processing with zero retention.",
      "You want translation between English, Portuguese, and Spanish so you can keep up in the moment.",
    ],
  },
  table: {
    heading: "Side by side",
    headers: { feature: "Feature", them: "Otter.ai", minuta: "Minuta" },
    rows: [
      {
        feature: "Live transcription",
        them: { value: "Yes — cloud", cite: { url: "https://otter.ai/pricing", capturedAt: "2026-04-30" } },
        minuta: { value: "Yes — Deepgram primary, OpenAI/Groq fallback" },
      },
      {
        feature: "Live translation (EN ↔ PT/ES)",
        them: {
          value: "No (no Portuguese support)",
          cite: {
            url: "https://help.otter.ai/hc/en-us/articles/26660468516631-Transcribe-a-conversation-in-Japanese-Spanish-French-or-English-US-or-UK",
            capturedAt: "2026-04-30",
          },
        },
        minuta: { value: "Yes — real-time" },
      },
      {
        feature: "Live answer drafting (copilot)",
        them: { value: "No" },
        minuta: { value: "Yes (Pro)" },
      },
      {
        feature: "Bot in the meeting",
        them: { value: "Yes — Otter Assistant joins the call", cite: { url: "https://otter.ai/pricing", capturedAt: "2026-04-30" } },
        minuta: { value: "No — no bot, no participant" },
      },
      {
        feature: "Audio processing",
        them: { value: "Cloud (US)", cite: { url: "https://otter.ai/blog/were-now-hipaa-compliant", capturedAt: "2026-04-30" } },
        minuta: { value: "Local capture, secure transient API" },
      },
      {
        feature: "Personal context for live answers",
        them: { value: "Limited" },
        minuta: { value: "Yes — copilot uses your docs" },
      },
      {
        feature: "Speaker identification",
        them: { value: "Yes" },
        minuta: { value: "Yes (post-call diarization)" },
      },
      {
        feature: "Auto summary / action items",
        them: { value: "Yes" },
        minuta: { value: "Yes — secure cloud LLM (Pro)" },
      },
      {
        feature: "Works offline", them: { value: "No" }, minuta: { value: "No (requires internet)" },
      },
      {
        feature: "Compliance",
        them: {
          value: "SOC 2 II + HIPAA (Enterprise)",
          cite: {
            url: "https://otter.ai/blog/otter-ai-achieves-soc-2-type-ii-attestation-report",
            capturedAt: "2026-04-30",
          },
        },
        minuta: { value: "Privacy-first by design (DPA on roadmap)" },
      },
      {
        feature: "Free tier",
        them: { value: "300 min/month", cite: { url: "https://otter.ai/pricing", capturedAt: "2026-04-30" } },
        minuta: { value: "7-day full trial · then 1h/month Free" },
      },
      {
        feature: "Paid",
        them: { value: "From $8.33/user/mo (annual)", cite: { url: "https://otter.ai/pricing", capturedAt: "2026-04-30" } },
        minuta: { value: "€9/mo" },
      },
    ],
    footnote: "Pricing as of 2026-04-30; check each vendor for the current numbers.",
  },
  whenMinuta: {
    heading: "Where Minuta wins",
    bullets: [
      "Use Otter when the value is the artifact after the meeting: notes, transcripts, search, and team follow-up.",
      "Use Minuta when the risk is the live moment: a customer asks in English, Portuguese, or Spanish, you need context, and you need a draft answer before the conversation moves on.",
      "Minuta grounds that draft in what was just said and in your own PDFs or notes, so the answer is not generic.",
      "The live translation rail helps you stay with the conversation while the transcript remains the source for the answer.",
    ],
  },
  also: {
    heading: "Comparing other tools?",
    links: [
      { label: "Fireflies", href: "/compare/fireflies-vs-minuta" },
      { label: "Granola", href: "/compare/granola-vs-minuta" },
      { label: "Zoom AI Companion", href: "/compare/zoom-ai-companion-vs-minuta" },
      { label: "Krisp Notes", href: "/compare/krisp-vs-minuta" },
    ],
  },
  cta: {
    heading: "Try Minuta on your next multilingual customer call",
    body: "Use it when the customer asks a hard question and you need a live answer draft, not just notes afterward.",
    primary: "Start free trial",
    secondary: "Download for macOS",
  },
};

const pt: ComparePageCopy = {
  seo: {
    title: "Otter.ai vs. Minuta — preço no Brasil e resposta ao vivo",
    description:
      "Comparação honesta entre Otter.ai e Minuta para brasileiros: preço em BRL, bot na call, suporte PT-BR e resposta ao vivo em inglês, português e espanhol.",
    keywords:
      "otter ai português, alternativa otter ai português, otter vs minuta, preço otter no brasil, ia para reunião em inglês, copiloto de reuniões",
  },
  vendor: VENDOR,
  eyebrow: "Comparação",
  h1: "Otter.ai vs. Minuta: notas depois, ou resposta ao vivo?",
  intro:
    "O Otter ajuda times a capturar, resumir, buscar e compartilhar o que aconteceu depois da reunião. O Minuta foi feito para quem precisa responder ao vivo em inglês, português ou espanhol, sem colocar bot na call e sem pagar em dólar.",
  honesty: {
    heading: "Escolha pelo trabalho",
    pickThem: {
      name: "Otter.ai",
      bullets: [
        "Você quer notas de reunião, transcripts, resumos e histórico pesquisável depois da call.",
        "Você não se incomoda com um bot entrando na call (Otter Assistant no Zoom/Meet/Teams).",
        "Seu time já vive no app web do Otter para busca, compartilhamento e workflows de follow-up.",
        "Você aceita pagar em USD e comparar custo por usuário antes de IOF e variação cambial.",
        "Você não precisa de ajuda para formular uma resposta enquanto a call ainda acontece.",
      ],
    },
    pickMinuta: [
      "Você conduz calls com clientes em inglês, português ou espanhol e precisa de ajuda ao vivo para responder.",
      "Você precisa formular uma resposta clara ao vivo, usando o transcript e seus próprios docs.",
      "Você não pode (ou não quer) bot aparecendo em call de cliente.",
      "Você quer pagar em Real: Founding 100 por R$22.50/mês nas primeiras 100 vagas, travado por 12 meses.",
      "Quer tradução entre inglês, português e espanhol ao lado do transcript para acompanhar a conversa no momento.",
    ],
  },
  table: {
    heading: "Preço e decisão lado a lado",
    headers: { feature: "Recurso", them: "Otter.ai", minuta: "Minuta" },
    rows: [
      {
        feature: "Preço no Brasil",
        them: {
          value: "Business: US$19.99/usuário/mês anual (aprox. R$100, antes de IOF)",
          cite: { url: "https://otter.ai/pricing", capturedAt: "2026-05-21" },
        },
        minuta: { value: "Founding 100: R$22.50/mês por 12 meses; depois Pro R$35/mês" },
      },
      {
        feature: "Cobra em Real",
        them: { value: "Não — cobrança em USD" },
        minuta: { value: "Sim — Stripe BR em BRL" },
      },
      {
        feature: "Transcrição ao vivo",
        them: { value: "Sim — na nuvem", cite: { url: "https://otter.ai/pricing", capturedAt: "2026-04-30" } },
        minuta: { value: "Sim — Deepgram primário, fallback OpenAI/Groq" },
      },
      {
        feature: "Tradução ao vivo (EN ↔ PT/ES)",
        them: {
          value: "Não (não transcreve português)",
          cite: {
            url: "https://help.otter.ai/hc/en-us/articles/26660468516631-Transcribe-a-conversation-in-Japanese-Spanish-French-or-English-US-or-UK",
            capturedAt: "2026-04-30",
          },
        },
        minuta: { value: "Sim — tempo real" },
      },
      {
        feature: "Rascunho de resposta ao vivo (copiloto)",
        them: { value: "Não" },
        minuta: { value: "Sim (Pro)" },
      },
      {
        feature: "Bot na reunião",
        them: { value: "Sim — Otter Assistant entra na call", cite: { url: "https://otter.ai/pricing", capturedAt: "2026-04-30" } },
        minuta: { value: "Não — sem bot, sem participante" },
      },
      {
        feature: "Processamento do áudio",
        them: { value: "Nuvem (EUA)", cite: { url: "https://otter.ai/blog/were-now-hipaa-compliant", capturedAt: "2026-04-30" } },
        minuta: { value: "Captura local, API temporária segura" },
      },
      {
        feature: "Contexto pessoal para respostas ao vivo",
        them: { value: "Limitado" },
        minuta: { value: "Sim — copiloto usa seus docs" },
      },
      {
        feature: "Identificação de falantes",
        them: { value: "Sim" },
        minuta: { value: "Sim (diarização pós-call)" },
      },
      {
        feature: "Resumo / action items automáticos",
        them: { value: "Sim" },
        minuta: { value: "Sim — cloud LLM seguro (Pro)" },
      },
      {
        feature: "Funciona offline", them: { value: "Não" }, minuta: { value: "Não (precisa de internet)" },
      },
      {
        feature: "Compliance",
        them: {
          value: "SOC 2 II + HIPAA (Enterprise)",
          cite: {
            url: "https://otter.ai/blog/otter-ai-achieves-soc-2-type-ii-attestation-report",
            capturedAt: "2026-04-30",
          },
        },
        minuta: { value: "Privacidade por design (DPA no roadmap)" },
      },
      {
        feature: "Plano grátis",
        them: { value: "300 min/mês", cite: { url: "https://otter.ai/pricing", capturedAt: "2026-04-30" } },
        minuta: { value: "7 dias com tudo · depois 1h/mês no Free" },
      },
      {
        feature: "Pago",
        them: { value: "Pro desde US$8.33; Business US$19.99/usuário/mês anual", cite: { url: "https://otter.ai/pricing", capturedAt: "2026-05-21" } },
        minuta: { value: "Pro R$35/mês; Founding 100 R$22.50/mês por 12 meses" },
      },
    ],
    footnote: "Preços dos concorrentes em USD consultados em 2026-05-21; conversão BRL aproximada, antes de IOF e variação cambial.",
  },
  whenMinuta: {
    heading: "Onde o Minuta ganha",
    bullets: [
      "Use Otter quando o valor é o artefato depois da reunião: notas, transcripts, busca e follow-up do time.",
      "Use Minuta quando o risco está no momento ao vivo: o cliente pergunta em inglês, português ou espanhol, você precisa de contexto e precisa de um rascunho antes da conversa andar.",
      "O Minuta fundamenta esse rascunho no que acabou de ser dito e nos seus próprios PDFs ou notas, para a resposta não sair genérica.",
      "A faixa de tradução ao vivo ajuda você a acompanhar a conversa enquanto o transcript continua sendo a base da resposta.",
    ],
  },
  also: {
    heading: "Comparando outras ferramentas?",
    links: [
      { label: "Fireflies", href: "/compare/fireflies-vs-minuta" },
      { label: "Granola", href: "/compare/granola-vs-minuta" },
      { label: "Zoom AI Companion", href: "/compare/zoom-ai-companion-vs-minuta" },
      { label: "Krisp Notes", href: "/compare/krisp-vs-minuta" },
    ],
  },
  cta: {
    heading: "Teste o Minuta na sua próxima call multilíngue com cliente",
    body: "Use quando o cliente fizer uma pergunta difícil e você precisar de um rascunho ao vivo, não só de notas depois.",
    primary: "Começar trial grátis",
    secondary: "Baixar pro macOS",
  },
};

const es: ComparePageCopy = {
  seo: {
    title: "Otter.ai vs. Minuta — notas después o respuesta en vivo",
    description:
      "Comparación honesta entre Otter.ai y Minuta. Elige Otter para notas, transcripts, búsqueda y workflows de equipo. Elige Minuta cuando necesitas responder en vivo en inglés, portugués o español.",
  },
  vendor: VENDOR,
  eyebrow: "Comparación",
  h1: "Otter.ai vs. Minuta: notas después, o respuesta en vivo?",
  intro:
    "Otter ayuda a los equipos a capturar, resumir, buscar y compartir lo que pasó después de la reunión. Minuta ayuda a profesionales a entender la sala y formular respuesta mientras el cliente todavía espera.",
  honesty: {
    heading: "Elige por el trabajo",
    pickThem: {
      name: "Otter.ai",
      bullets: [
        "Quieres notas de reunión, transcripts, resúmenes e historial searchable después de la call.",
        "No te molesta un bot entrando en la call (Otter Assistant en Zoom/Meet/Teams).",
        "Tu equipo ya vive en la web de Otter para búsqueda, compartir y workflows de follow-up.",
        "No necesitas ayuda para formular una respuesta mientras la call sigue ocurriendo.",
      ],
    },
    pickMinuta: [
      "Llevas calls con clientes en inglés, portugués o español y necesitas ayuda en vivo para responder.",
      "Necesitas formular una respuesta clara en vivo, usando el transcript y tus propios docs.",
      "No puedes (o no quieres) que aparezca un bot en calls de cliente.",
      "Tu audio es sensible — quieres procesamiento seguro con retención cero.",
      "Quieres traducción entre inglés, portugués y español junto al transcript para seguir la conversación en el momento.",
    ],
  },
  table: {
    heading: "Lado a lado",
    headers: { feature: "Función", them: "Otter.ai", minuta: "Minuta" },
    rows: [
      {
        feature: "Transcripción en vivo",
        them: { value: "Sí — nube", cite: { url: "https://otter.ai/pricing", capturedAt: "2026-04-30" } },
        minuta: { value: "Sí — Deepgram primario, fallback OpenAI/Groq" },
      },
      {
        feature: "Traducción en vivo (EN ↔ PT/ES)",
        them: {
          value: "No (sin soporte de portugués)",
          cite: {
            url: "https://help.otter.ai/hc/en-us/articles/26660468516631-Transcribe-a-conversation-in-Japanese-Spanish-French-or-English-US-or-UK",
            capturedAt: "2026-04-30",
          },
        },
        minuta: { value: "Sí — tiempo real" },
      },
      {
        feature: "Borrador de respuesta en vivo (copiloto)",
        them: { value: "No" },
        minuta: { value: "Sí (Pro)" },
      },
      {
        feature: "Bot en la reunión",
        them: { value: "Sí — Otter Assistant entra en la call", cite: { url: "https://otter.ai/pricing", capturedAt: "2026-04-30" } },
        minuta: { value: "No — sin bot, sin participante" },
      },
      {
        feature: "Procesamiento del audio",
        them: { value: "Nube (EE.UU.)", cite: { url: "https://otter.ai/blog/were-now-hipaa-compliant", capturedAt: "2026-04-30" } },
        minuta: { value: "Captura local, API transitoria segura" },
      },
      {
        feature: "Contexto personal para respuestas en vivo",
        them: { value: "Limitado" },
        minuta: { value: "Sí — el copiloto usa tus docs" },
      },
      {
        feature: "Identificación de locutores",
        them: { value: "Sí" },
        minuta: { value: "Sí (diarización post-call)" },
      },
      {
        feature: "Resumen / action items automáticos",
        them: { value: "Sí" },
        minuta: { value: "Sí — cloud LLM seguro (Pro)" },
      },
      {
        feature: "Funciona offline", them: { value: "No" }, minuta: { value: "No (requiere internet)" },
      },
      {
        feature: "Compliance",
        them: {
          value: "SOC 2 II + HIPAA (Enterprise)",
          cite: {
            url: "https://otter.ai/blog/otter-ai-achieves-soc-2-type-ii-attestation-report",
            capturedAt: "2026-04-30",
          },
        },
        minuta: { value: "Privacidad por diseño (DPA en roadmap)" },
      },
      {
        feature: "Plan gratis",
        them: { value: "300 min/mes", cite: { url: "https://otter.ai/pricing", capturedAt: "2026-04-30" } },
        minuta: { value: "7 días con todo · luego 1h/mes en Gratis" },
      },
      {
        feature: "De pago",
        them: { value: "Desde US$ 8,33/usuario/mes (anual)", cite: { url: "https://otter.ai/pricing", capturedAt: "2026-04-30" } },
        minuta: { value: "€9/mes" },
      },
    ],
    footnote: "Precios referencia 2026-04-30; confirma con cada empresa los valores actuales.",
  },
  whenMinuta: {
    heading: "Dónde gana Minuta",
    bullets: [
      "Usa Otter cuando el valor es el artefacto después de la reunión: notas, transcripts, búsqueda y follow-up del equipo.",
      "Usa Minuta cuando el riesgo está en el momento en vivo: el cliente pregunta en inglés, portugués o español, necesitas contexto y necesitas un borrador antes de que la conversación avance.",
      "Minuta basa ese borrador en lo que se acaba de decir y en tus propios PDFs o notas, para que la respuesta no sea genérica.",
      "La franja de traducción en vivo te ayuda a seguir la conversación mientras el transcript sigue siendo la base de la respuesta.",
    ],
  },
  also: {
    heading: "¿Comparando otras herramientas?",
    links: [
      { label: "Fireflies", href: "/compare/fireflies-vs-minuta" },
      { label: "Granola", href: "/compare/granola-vs-minuta" },
      { label: "Zoom AI Companion", href: "/compare/zoom-ai-companion-vs-minuta" },
      { label: "Krisp Notes", href: "/compare/krisp-vs-minuta" },
    ],
  },
  cta: {
    heading: "Prueba Minuta en tu próxima call multilingüe con cliente",
    body: "Úsalo cuando el cliente haga una pregunta difícil y necesites un borrador en vivo, no solo notas después.",
    primary: "Empezar prueba gratis",
    secondary: "Descargar para macOS",
  },
};

export const compareOtterCopy: ComparePageCopyByLang = { en, pt, es };
